import {
  ExportDropdown,
  DeleteConfirmation,
  ExportDialog,
  SearchInput,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { mimeTypes } from '~/utils';
import { ActiveStatusSelect } from '../custom-data/components';
import PaymentTermDrawer from './PaymentTermDrawer';
import PaymentTermDrawerView from './PaymentTermDrawerView';

function PaymentTermsPage() {
  const documentTitle = useDocumentTitle('Payment Terms');

  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', isActive: 'true' });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .paymentTerms()
        .get({ ...params, isActive: params.isActive ?? undefined });
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .paymentTerms()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (paymentTerm) => {
    setDialog(
      <PaymentTermDrawer
        paymentTerm={paymentTerm}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Payment Terms');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  const handleView = async (paymentTerm) => {
    setDialog(
      <PaymentTermDrawerView
        paymentTerm={paymentTerm}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Payment Terms');
        }}
      />,
    );
  };

  async function handleActiveStatusChange(item, flag) {
    try {
      await api.www.workspaces(workspace.id).paymentTerms(item.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDelete(paymentTerm) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete these Payment Terms?</DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .paymentTerms(paymentTerm ? paymentTerm.id : undefined)
        .delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Payment Terms</Page.Title>
          <Page.Description>Payment terms are used to set the due date of invoices.</Page.Description>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('payment_terms.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('payment_terms.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="plus" tooltip="New Payment Terms" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />

          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column>Name</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((paymentTerm) => {
              const { name, isActive, id, isDefault } = paymentTerm;

              return (
                <ListView.Row
                  onClick={() => (isDefault ? handleView(paymentTerm) : handleEdit(paymentTerm))}
                  key={id}
                  isDisabled={!isActive}>
                  <ListView.Cell>{name}</ListView.Cell>
                  <ListViewActions>
                    {!isDefault ? (
                      <>
                        <ListViewActions.Edit onClick={() => handleEdit(paymentTerm)} />
                        <hr />

                        <ListViewMenu>
                          {({ setIsOpen }) => (
                            <>
                              <ListViewMenu.Item onClick={() => handleEdit(paymentTerm)}>Edit</ListViewMenu.Item>
                              <ListViewMenu.Item
                                onClick={async () => {
                                  await handleActiveStatusChange(paymentTerm, !isActive);
                                  setIsOpen(false);
                                }}>
                                {isActive ? 'Deactivate' : 'Activate'}
                              </ListViewMenu.Item>
                              <ListViewMenu.DeleteItem
                                tooltip="This item is currently in use."
                                onCheckDependencies={async (workspace) =>
                                  (await workspace.paymentTerms(id).hasDependencies()).data
                                }
                                onClick={() => handleDelete(paymentTerm)}>
                                Delete
                              </ListViewMenu.DeleteItem>
                            </>
                          )}
                        </ListViewMenu>
                      </>
                    ) : (
                      <ListViewActions.View onClick={() => handleView(paymentTerm)} />
                    )}
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Payment Terms" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default PaymentTermsPage;
